import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
  Legend,
} from "recharts";
import { useMemo, useState } from "react";
import _ from "lodash";

import styles from "./style.module.scss";
import { CHART_COLORS, getRandomHexColor } from "../../../../../services/colorUtil";
import { formatAxisTick, formatChartNumber } from "../chartNumberFormat";

interface SeriesData {
  label: string;
  x: (string | number)[];
  y: number[];
}

interface ChartArtifacts {
  meta: {
    xlabel: string;
    ylabel: string;
    title?: string;
  };
  series: SeriesData[];
}

interface LineChartViewProps {
  artifacts: ChartArtifacts;
}

interface RenderLegendProps {
  data: any;
  activeLine: string | null;
  onLegendClick: (lineName: string) => void;
}

const chartStyles = {
  tickLine: { stroke: "#64748b", strokeWidth: 1 },
  axisLine: { stroke: "#64748b", strokeWidth: 1 },
  tickStyle: { fill: "#64748b", fontSize: 12, fontWeight: 500 },
  contentStyle: {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    fontSize: "14px",
    fontWeight: 500,
  },
  labelStyle: {
    color: "#374151",
    fontWeight: 600,
    padding: 0,
  },
};

const LineChartView: React.FC<LineChartViewProps> = ({ artifacts }) => {
  const [activeLine, setActiveLine] = useState<string | null>(null);

  const prepared = useMemo(() => {
    if (!artifacts?.meta || !artifacts?.series?.length) return null;

    const { xlabel, ylabel } = artifacts.meta;
    const series = artifacts.series;

    const allXValues = _.uniq(_.flatMap(series, (s: SeriesData) => s.x)).sort();

    const data = allXValues.map((xValue) => {
      const point: Record<string, string | number> = { x: xValue };
      series.forEach((s: SeriesData) => {
        const index = s.x.indexOf(xValue);
        if (index !== -1) {
          point[s.label] = s.y[index];
        }
      });
      return point;
    });

    return { xlabel, ylabel, series, data };
  }, [artifacts]);

  if (!prepared) {
    return <div className={styles.noDataMessage}>No chart data available</div>;
  }

  const { xlabel, ylabel, series, data } = prepared;

  const calculateOpacity = (label: string) => {
    if (activeLine === null) return 1;

    if (activeLine === label) return 1;

    return 0.15;
  };

  const handleLegendClick = (label: string) => {
    if (activeLine === label) {
      setActiveLine(null);
      return;
    }
    setActiveLine(label);
  };

  const tooltipFormatter = (value: number, name: string) => {
    if (activeLine === null) {
      return [formatChartNumber(Number(value), "fixed2"), name];
    }

    if (activeLine !== null && activeLine === name) {
      return [formatChartNumber(Number(value), "fixed2"), name];
    }

    return null;
  };

  return (
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={data}
          margin={{
            top: 30,
            right: 40,
            left: 30,
            bottom: 70,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e2e8f0"
            strokeOpacity={0.6}
          />
          <XAxis
            dataKey="x"
            axisLine={chartStyles.axisLine}
            tickLine={chartStyles.tickLine}
            tick={chartStyles.tickStyle}
          >
            <Label
              value={xlabel}
              position="insideBottom"
              offset={-15}
              className={styles.label}
            />
          </XAxis>
          <YAxis
            axisLine={chartStyles.axisLine}
            tickLine={chartStyles.tickLine}
            tick={chartStyles.tickStyle}
            tickFormatter={formatAxisTick}
          >
            <Label
              value={ylabel}
              angle={-90}
              position="left"
              offset={8}
              className={styles.label}
            />
          </YAxis>
          <Tooltip
            contentStyle={chartStyles.contentStyle}
            labelStyle={chartStyles.labelStyle}
            allowEscapeViewBox={{ x: false, y: false }}
            formatter={(value: number, name: string) =>
              tooltipFormatter(value, name)
            }
            itemSorter={(item) => -(Number(item.value) || 0)}
            wrapperStyle={{ pointerEvents: "auto" }}
          />

          <Legend
            verticalAlign="top"
            content={(data) => (
              <LegendView
                data={data}
                activeLine={activeLine}
                onLegendClick={handleLegendClick}
              />
            )}
            wrapperStyle={{ fontSize: "13px" }}
          />

          {series.map((s: SeriesData, i: number) => (
            <>
              <Line
                key={i}
                type="monotone"
                dataKey={s.label}
                stroke={
                  i <= _.size(CHART_COLORS)
                    ? CHART_COLORS[i]
                    : getRandomHexColor()
                }
                dot={false}
                strokeWidth={2}
                opacity={calculateOpacity(s.label)}
                activeDot={
                  activeLine === null || activeLine === s.label
                    ? {
                        r: 6,
                        fill:
                          i <= _.size(CHART_COLORS)
                            ? CHART_COLORS[i]
                            : getRandomHexColor(),
                        stroke: "white",
                        strokeWidth: 2,
                      }
                    : false
                }
              />
            </>
          ))}
          <text x="100%" y="95%" textAnchor="end" dominantBaseline="auto" className={styles.disclaimer}>* AI Generated Graph</text>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const LegendView: React.FC<RenderLegendProps> = ({
  data,
  activeLine,
  onLegendClick,
}) => {
  const { payload } = data;

  return (
    <ul className={styles.legendContainer}>
      {payload.map((entry: any, index: number) => {
        const isActive = activeLine === null || activeLine === entry.value;

        return (
          <li
            key={`item-${index}`}
            onClick={() => onLegendClick(entry.value)}
            className={styles.legendList}
            style={{
              color: isActive ? entry.color : "#cbd5e1",
            }}
          >
            <span
              className={styles.legendName}
              style={{
                backgroundColor: isActive ? entry.color : "#cbd5e1",
              }}
            />
            {entry.value}
          </li>
        );
      })}
    </ul>
  );
};

export default LineChartView;
